/// <amd-module name="Wiki/Editor/ErrorHelper" />
import { format } from "VSS/Core/Util/String";
import { IVssPageContext } from "VSS/Platform/Context";
import { VssServerError } from "VSS/Platform/Fetch";
import { combineUrlPaths } from "VSS/Platform/Util/Url";

import { GlobalMessageLevel, IGlobalMessagesService } from "TFS/GlobalBanner/GlobalMessagesService";
import * as Resources from "Wiki/Editor/Resources";
import { PathConstants } from "Wiki/ViewCommon/Constants";

export const enum WikiErrorNames {
    gitItemNotFoundException = "GitItemNotFoundException",
    wikiPageNotFoundException = "WikiPageNotFoundException",
    wikiAncestorPageNotFoundException = "WikiAncestorPageNotFoundException",
    wikiPageHasConflictsException = "WikiPageHasConflictsException",
    wikiPageRenameSourceNotFoundException = "WikiPageRenameSourceNotFoundException",
    gitObjectRejectedException = "GitObjectRejectedException",
    wikiPageAlreadyExistsException = "WikiPageAlreadyExistsException",
    parentPageContentUnavailableException = "ParentPageContentUnavailableException"
}

export function getWikiUpdateError(error: Error, pagePath: string, originalPagePath?: string): Error {
    const vssServerError = error as VssServerError;
    if (!vssServerError || !vssServerError.serverError) {
        return vssServerError;
    }

    let errorMessage: string | undefined;
    const errorName = vssServerError.serverError.typeKey;

    switch (errorName) {
        case WikiErrorNames.gitItemNotFoundException:
        case WikiErrorNames.wikiPageNotFoundException:
            errorMessage = format(Resources.ErrorMessage_PageConflict_Deleted, pagePath);
            break;
        case WikiErrorNames.wikiAncestorPageNotFoundException:
            errorMessage = format(Resources.ErrorMessage_AncestorConflicts, pagePath);
            break;
        case WikiErrorNames.wikiPageHasConflictsException:
            errorMessage = format(Resources.ErrorMessage_PageConflict_Updated, pagePath);
            break;
        case WikiErrorNames.wikiPageRenameSourceNotFoundException:
            errorMessage = format(Resources.ErrorMessage_PageConflict_Deleted, originalPagePath);
            break;
        case WikiErrorNames.gitObjectRejectedException:
            if (vssServerError.message.indexOf("VS403658") !== -1) {
                // File Size Policy Error
                errorMessage = Resources.ErrorMessage_FileSizePolicy;
            }
            break;
        case WikiErrorNames.wikiPageAlreadyExistsException:
            errorMessage = format(Resources.ErrorMessage_PageConflict_AlreadyAdded, pagePath);
            break;
        default:
            break;
    }

    return errorMessage ? _getError(errorName, errorMessage) : vssServerError;
}

export function getPageTitleValidationError(parentPath: string, pageTitle: string): string | undefined {
    const dotCharacter = ".";
    pageTitle = pageTitle.trim();
    if (!pageTitle) {
        return Resources.PageTitleEmpty;
    }

    const firstChar = pageTitle[0];
    const lastChar = pageTitle[pageTitle.length - 1];
    if (firstChar === dotCharacter || lastChar === dotCharacter) {
        return Resources.PageTitleInvalid;
    }

    if (
        !PathConstants.ResourceNameInvalidCharacters.every(character => pageTitle.indexOf(character) == -1) ||
        !PathConstants.PageNameReservedCharacters.every(character => pageTitle.indexOf(character) == -1)
    ) {
        return Resources.PageTitleInvalidCharacter;
    }

    const fullPagePath = combineUrlPaths(parentPath, pageTitle);
    if (fullPagePath && fullPagePath.length > PathConstants.MaximumPagePathLength) {
        return format(Resources.PagePathTooLong, PathConstants.MaximumPagePathLength);
    }

    return undefined;
}

function _getError(errorName: string, errorMessage: string): Error {
    const newError = new Error(errorMessage);
    newError.name = errorName;

    return newError;
}
